/**
 * Turns `ALL_PRE_CONSULTATION_APPOINTMENTS_QUERY` results into queue rows.
 *
 * The query is rooted on the appointment, so one appointment yields one row and
 * the patient hangs off it — the inverse of every other tab on this page. That
 * inversion is what makes membership "has a call or a consult on the books"
 * rather than "carries a stage tag", and it is why this file exists instead of
 * another branch inside `usePatientList`'s already-long `patientList` memo.
 *
 * Everything here is a pure transform over query output. The rules it applies —
 * membership, status, likelihood — live in `@bariendo/lib/pac-pre-consultation-list`
 * so they are testable without a table, a client, or React.
 */

import { Appointment, HumanName, Practitioner } from '@medplum/fhirtypes';
import { ActiveLeadState } from '@bariendo/lib/active-lead';
import { getCareTeamMembersInPool } from '@bariendo/lib/care-team-roles';
import { Track } from '@bariendo/lib/constants';
import { getTrackOrSelfPay } from '@bariendo/lib/episode-of-care';
import { readEmployer } from '@bariendo/lib/pac-inquiry-list';
import { readInterestedService, readMarketLocation } from '@bariendo/lib/intake-selection';
import { isCiIntroCallAppointment, isConsultationAppointment } from '@bariendo/lib/patient-access';
import { PacIntroCallAnswers, parseCiIntroCallAnswers } from '@bariendo/lib/patient-access-note';
import {
  getIntroCallLikelihood,
  introCallStatusLabel,
  isPreConsultationLead,
  IntroCallStatus,
  LikelihoodCell,
} from '@bariendo/lib/pac-pre-consultation-list';
import { readEligibilityIntakeSelections, readEligibilityPayment } from '@bariendo/lib/eligibility-intake';
import { hasOverdueTask } from '@bariendo/lib/pac-post-consultation-list';
import { appointmentIsUpcoming } from '@bariendo/lib/pac-list-appointments';
import {
  activeLeadFor,
  hydratedAppointments,
  firstPractitionerName,
  PacQueueAppointmentResult,
  LastCommunicationCell,
  NextTaskCell,
  patientParticipant,
  PreConsultationPatientResult,
  StickyNoteCell,
  toLastCommunicationCell,
  toNextTaskCell,
  toStickyNoteCell,
} from './listRowShared';
import {
  PacQueueHydration,
  hydratedAppointmentPractitioner,
  hydratedPractitionerNames,
} from '../hooks/usePacQueueHydration';

export type {
  ConsultAppointmentResult,
  PacQueueAppointmentResult,
  LastCommunicationCell,
  LastCommunicationResult,
  PreConsultationPatientResult,
} from './listRowShared';

export interface PreConsultationLeadRow {
  id: string;
  patient: PreConsultationPatientResult;
  /** Absent for a lead who self-scheduled a consult without a PAC call first. */
  introCall?: PacQueueAppointmentResult;
  /** The PAC rep who ran the call — whose read the Likelihood column reports. */
  rep?: HumanName;
  consult?: Appointment | PacQueueAppointmentResult;
  /** The NP who ran or will run the consult. */
  np?: HumanName;
  answers?: PacIntroCallAnswers;
  location?: string;
  /** The offering they are interested in, as stored: `ESG`, `IGB`, `TORe`. */
  procedure?: string;
  /** Hub rows only, from here to `stickyNote`. */
  track?: Track;
  employer?: string;
  patientNavigators?: HumanName[];
  patientNavigatorPractitioners?: Practitioner[];
  stickyNote?: StickyNoteCell;
  likelihood: LikelihoodCell;
  /** Absent on a consult-rooted row: the column reports the intro call. */
  status?: IntroCallStatus;
  lastCommunication?: LastCommunicationCell;
  nextTask?: NextTaskCell;
  /** Any open task past its due date — for the row's own cell colour, not a chip. */
  hasOverdueTask: boolean;
  activeLead: ActiveLeadState;
}

/**
 * One row per rooted appointment — an intro call, or a consult for a lead who
 * never had one.
 *
 * A patient with two intro calls in range yields two rows. That is the honest
 * rendering for a queue keyed on calls, and it is what makes the *No-show* chip
 * work: collapsing to the latest call would hide an earlier no-show the moment a
 * second attempt connected.
 *
 * Consults are a SECOND pass, and only for patients no intro call already
 * covered. An intro-call row already reports the consult in its Consult date
 * column, so rooting on both would list that lead twice. Since Intake 1.1 the
 * second pass is the whole queue in production — no `ci-intro-call` has ever
 * been booked there — but the order is what keeps a legacy lead single-rowed.
 */
export function buildPreConsultationRows(
  appointments: PacQueueAppointmentResult[],
  hydration: PacQueueHydration,
  now: Date
): PreConsultationLeadRow[] {
  const rows: PreConsultationLeadRow[] = [];
  const patientsWithIntroCall = new Set<string>();

  for (const introCall of appointments) {
    const patient = patientParticipant(introCall.participant);

    if (!patient?.id || !introCall.id) {
      continue;
    }

    // The query returns consults too; they are the second pass. A consult must
    // never take the intro-call path, which would put it in the Intro call,
    // Status and Likelihood columns as if a rep had run it.
    if (!isCiIntroCallAppointment(introCall)) {
      continue;
    }

    const patientRef = `Patient/${patient.id}`;
    patientsWithIntroCall.add(patientRef);
    const consult = hydration.consultByPatient.get(patientRef);

    if (!isPreConsultationLead({ introCall, consult }, now)) {
      continue;
    }

    const note = hydration.introCallNoteByAppointment.get(`Appointment/${introCall.id}`);
    const answers = note ? parseCiIntroCallAnswers(note) : undefined;
    const openTasks = hydration.tasksByPatient.get(patientRef);
    const lastCommunication = hydration.lastCommunicationByPatient.get(patientRef);

    rows.push({
      id: introCall.id,
      patient,
      introCall,
      rep: firstPractitionerName(introCall.participant),
      consult,
      np: hydratedAppointmentPractitioner(consult, hydration.practitionerByRef),
      answers,
      // The rep's explicit edit outranks what was said on the call, which in turn
      // outranks what the lead's eligibility form inferred from their ZIP. Same
      // reader as the Intake & billing panel and the intro-call card, so the three
      // surfaces cannot disagree about one patient.
      location: readMarketLocation(
        patient,
        answers,
        readEligibilityIntakeSelections(hydration.eligibilityByPatient.get(patientRef)).market
      ),
      procedure: readInterestedService(
        patient,
        answers,
        readEligibilityIntakeSelections(hydration.eligibilityByPatient.get(patientRef)).procedure
      ),
      likelihood: getIntroCallLikelihood(answers),
      status: introCallStatusLabel(introCall, now),
      lastCommunication: toLastCommunicationCell(lastCommunication),
      nextTask: toNextTaskCell(openTasks, hydration.practitionerByRef),
      hasOverdueTask: hasOverdueTask(openTasks, now),
      activeLead: activeLeadFor({
        lastCommunicationSent: lastCommunication?.sent,
        leadFlags: hydration.flagsByPatient.get(patientRef),
        signupAt: hydration.episodeByPatient.get(patientRef)?.period?.start,
        appointments: [introCall, ...hydratedAppointments(hydration, patientRef)],
        now,
      }),
    });
  }

  for (const consult of appointments) {
    const patient = patientParticipant(consult.participant);

    if (!patient?.id || !consult.id || !isConsultationAppointment(consult as Appointment)) {
      continue;
    }

    const patientRef = `Patient/${patient.id}`;

    if (patientsWithIntroCall.has(patientRef) || !isPreConsultationLead({ consult }, now)) {
      continue;
    }

    const openTasks = hydration.tasksByPatient.get(patientRef);
    const lastCommunication = hydration.lastCommunicationByPatient.get(patientRef);

    rows.push({
      id: consult.id,
      patient,
      consult,
      // A rooted consult inlines its practitioner through the same selection an
      // intro call uses, so this needs no hydration lookup.
      np: firstPractitionerName(consult.participant),
      // No call means no charted answers: Status and Likelihood stay empty and
      // Location falls back to the extension, then the eligibility form.
      likelihood: getIntroCallLikelihood(undefined),
      location: readMarketLocation(
        patient,
        undefined,
        readEligibilityIntakeSelections(hydration.eligibilityByPatient.get(patientRef)).market
      ),
      procedure: readInterestedService(
        patient,
        undefined,
        readEligibilityIntakeSelections(hydration.eligibilityByPatient.get(patientRef)).procedure
      ),
      lastCommunication: toLastCommunicationCell(lastCommunication),
      nextTask: toNextTaskCell(openTasks, hydration.practitionerByRef),
      hasOverdueTask: hasOverdueTask(openTasks, now),
      activeLead: activeLeadFor({
        lastCommunicationSent: lastCommunication?.sent,
        leadFlags: hydration.flagsByPatient.get(patientRef),
        signupAt: hydration.episodeByPatient.get(patientRef)?.period?.start,
        appointments: [consult, ...hydratedAppointments(hydration, patientRef)],
        now,
      }),
    });
  }

  return rows;
}

/**
 * The Intake hub's rows (BAR-2811): one per patient, on their soonest upcoming
 * consult. No intro call, so no Status or Likelihood.
 */
export function buildUpcomingConsultRows(
  appointments: PacQueueAppointmentResult[],
  hydration: PacQueueHydration,
  now: Date
): PreConsultationLeadRow[] {
  const soonestByPatient = new Map<string, PacQueueAppointmentResult>();

  for (const consult of appointments) {
    const patient = patientParticipant(consult.participant);

    if (!patient?.id || !consult.id || !consult.start || !appointmentIsUpcoming(consult, now)) {
      continue;
    }

    const patientRef = `Patient/${patient.id}`;
    const soonest = soonestByPatient.get(patientRef);

    if (!soonest?.start || consult.start < soonest.start) {
      soonestByPatient.set(patientRef, consult);
    }
  }

  return [...soonestByPatient].map(([patientRef, consult]) => {
    const patient = patientParticipant(consult.participant) as PreConsultationPatientResult;
    const eligibility = hydration.eligibilityByPatient.get(patientRef);
    const selections = readEligibilityIntakeSelections(eligibility);
    const episode = hydration.episodeByPatient.get(patientRef);
    const openTasks = hydration.tasksByPatient.get(patientRef);
    const lastCommunication = hydration.lastCommunicationByPatient.get(patientRef);
    const navigatorRefs = getCareTeamMembersInPool(
      hydration.careTeamByPatient.get(patientRef),
      hydration.patientNavigatorPool
    );

    return {
      id: consult.id as string,
      patient,
      consult,
      np: firstPractitionerName(consult.participant),
      likelihood: getIntroCallLikelihood(undefined),
      location: readMarketLocation(patient, undefined, selections.market),
      procedure: readInterestedService(patient, undefined, selections.procedure),
      track: getTrackOrSelfPay(episode),
      employer: readEmployer(patient) ?? readEligibilityPayment(eligibility).employer,
      patientNavigators: hydratedPractitionerNames(navigatorRefs, hydration.practitionerByRef),
      patientNavigatorPractitioners: navigatorRefs
        .map((reference) => hydration.practitionerByRef.get(reference))
        .filter((practitioner): practitioner is Practitioner => practitioner !== undefined),
      stickyNote: toStickyNoteCell(hydration.stickyNoteByPatient.get(patientRef), hydration.practitionerByRef),
      lastCommunication: toLastCommunicationCell(lastCommunication),
      nextTask: toNextTaskCell(openTasks, hydration.practitionerByRef),
      hasOverdueTask: hasOverdueTask(openTasks, now),
      activeLead: activeLeadFor({
        lastCommunicationSent: lastCommunication?.sent,
        leadFlags: hydration.flagsByPatient.get(patientRef),
        signupAt: hydration.episodeByPatient.get(patientRef)?.period?.start,
        appointments: [consult, ...hydratedAppointments(hydration, patientRef)],
        now,
      }),
    };
  });
}
